import { z } from "zod";
import { canonicalTwinBoundaryJson } from "./twin-boundary-json.js";
/**
 * A question a workflow author asks Twin Judge about a review before it is raised, written as data rather than
 * Python so an editor can show and change it. `about` names the finding it judges (a workflow-owned field of the
 * review's dataset), or null for the whole review, whose judgment then lands on every finding. `ask` and
 * `instructions` may name `{value}` (the proposed value of `about`), `{fieldKey}` (the review's proposed value
 * for that field, or a source field's value on the record) and `{pattern:effectKey}` (the established pattern a
 * source read found, in words). `expects` is the answer the proposal needs: "yes" by default for a yes/no
 * question; for a choice, one of its options, or omitted to read the judge's own answer.
 */
export const workflowJudgeQuestionSchema = z.object({
    id: z.string().regex(/^[a-z][a-z0-9_]{0,39}$/u),
    about: z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/u).nullable(),
    ask: z.string().trim().min(1).max(2000),
    answer: z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("yes_no") }).strict(),
        z.object({ kind: z.literal("choice"), options: z.array(z.string().trim().min(1).max(100)).min(2).max(20) }).strict(),
    ]),
    expects: z.string().min(1).max(100).optional(),
    instructions: z.string().max(2000).optional(),
}).strict();
/** The id an authored question is asked under, beside the second reader's `finding_<n>` questions. */
export const workflowJudgeQuestionKey = (id) => `q_${id}`;
/** `{fieldKey}`, `{value}`, or `{pattern:effectKey}` for the established pattern a source read found. */
const PLACEHOLDER = /\{([A-Za-z_][A-Za-z0-9_]*(?::[A-Za-z_][A-Za-z0-9_]*)?)\}/gu;
/** The names a question's text refers to in braces. */
export function workflowJudgeQuestionPlaceholders(text) {
    return [...text.matchAll(PLACEHOLDER)].map((match) => match[1]);
}
/** The answer a question needs when its author named none: "yes" for a yes/no question, none for a choice. */
export function workflowJudgeQuestionExpects(question) {
    return question.expects ?? (question.answer.kind === "yes_no" ? "yes" : undefined);
}
/**
 * The Twin Judge question an authored question asks of a draft review, given the values it may name by field key
 * (the review's proposed values, the record's source fields and the patterns the run's source reads found). A
 * value is written as text, or with sorted keys when structured, so a replay asks the same question. Null when the
 * question names a value this run does not have, such as the pattern of a read that did not run: its answer would
 * judge the gap, not the review.
 */
export function compileWorkflowJudgeQuestion(question, known) {
    const texts = [question.ask, question.instructions ?? ""];
    const named = texts.flatMap(workflowJudgeQuestionPlaceholders).map((name) => name === "value" ? question.about : name);
    if (!named.every((field) => field && known.has(field)))
        return null;
    const fill = (text) => text.replace(PLACEHOLDER, (_, name) => {
        const value = known.get(name === "value" ? question.about : name);
        return (typeof value === "string" ? value : canonicalTwinBoundaryJson(value ?? null)).slice(0, 1500);
    });
    const instructions = [fill(question.ask).slice(0, 2000), question.instructions ? fill(question.instructions) : null]
        .filter(Boolean).join("\n");
    const criteria = question.answer.kind === "yes_no" ? { yes: "Yes.", no: "No." }
        : Object.fromEntries(question.answer.options.map((option) => [option, option]));
    return { type: "choice", instructions: instructions.slice(0, 8000), criteria };
}
